import { useEffect, useState } from "react";
import { X, Minus, Plus, MessageCircle, Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fieldErrors, fileUrl } from "@/lib/api";
import { useStore, getUtm } from "@/context/StoreContext";
import { brl, buildSacola, digits, maskCep, maskPhone, viaCep, waLink } from "@/lib/fmt";
import { packs } from "./BundleOffer";
import { FreeShipBar } from "./CartDrawer";
import PaymentPanel, { copyText } from "./PaymentPanel";

function ProductRow({ p }) {
  const { add } = useStore();
  const [n, setN] = useState(1);
  const pk = p.forma === "kit" ? [{ n: 1, d: 0, total: p.preco }] : packs(p);
  const sel = pk[n - 1];
  return (
    <li className="flex gap-3 py-4" data-testid={`quick-product-${p.slug}`}>
      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-na-sage2"><img src={fileUrl(p.imagens?.[0])} alt="" className="prod-img h-16 object-contain" /></div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{p.nome}</p>
        <p className="line-clamp-1 text-xs text-na-ink2">{p.alegacoes?.[0]}</p>
        <p className="mt-1 text-sm">{p.preco_de > p.preco && <span className="mr-1 text-xs text-na-ink2 line-through">{brl(p.preco_de)}</span>}<b>{brl(p.preco)}</b></p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {pk.length > 1 && pk.map((k) => (
            <button key={k.n} onClick={() => setN(k.n)} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${n === k.n ? "border-na-forest bg-na-forest text-white" : "border-na-line"}`} data-testid={`quick-units-${p.slug}-${k.n}`}>
              {k.n} un{k.d ? ` -${k.d}%` : ""}
            </button>
          ))}
          <button className="btn-na ml-auto px-3 py-1.5 text-xs" disabled={p.estoque < n} onClick={() => { add(p.slug, n, 1, false, "none", sel.total); toast.success("Adicionado à sacola"); }} data-testid={`quick-add-${p.slug}`}>Adicionar · {brl(sel.total)}</button>
        </div>
      </div>
    </li>
  );
}

function OrderForm({ onDone }) {
  const { items, pricing, settings, setItems, sessao } = useStore();
  const [f, setF] = useState({ nome: "", celular: "", cep: "", numero: "", rua: "", complemento: "", bairro: "", cidade: "", uf: "", obs: "" });
  const [metodo, setMetodo] = useState("pix");
  const [ciencia, setCiencia] = useState(false);
  const [errs, setErrs] = useState({});
  const [pr, setPr] = useState(pricing);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/cart/price", { itens: items, cep: digits(f.cep).length === 8 ? digits(f.cep) : null, metodo }).then((r) => setPr(r.data));
  }, [items, f.cep, metodo]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onCep = async (e) => {
    const v = maskCep(e.target.value);
    setF((x) => ({ ...x, cep: v }));
    const a = await viaCep(v);
    if (a) setF((x) => ({ ...x, ...a }));
  };
  const validate = () => {
    const e = {};
    if (f.nome.trim().split(/\s+/).length < 2) e.nome = "Informe nome e sobrenome";
    if (digits(f.celular).length < 10) e.celular = "Celular com DDD";
    if (digits(f.cep).length !== 8) e.cep = "CEP inválido";
    if (!f.numero.trim()) e.numero = "Informe o número";
    if (!f.rua.trim()) e.rua = "Informe o endereço";
    if (pr?.restrito && !ciencia) e.ciencia_advertencias = "Confirme que leu as advertências";
    setErrs(e);
    return !Object.keys(e).length;
  };
  const send = async (canal) => {
    if (!validate()) return;
    setBusy(true);
    const waWin = canal === "whatsapp" ? window.open("", "_blank") : null;
    try {
      const r = await api.post("/orders", {
        itens: items, cliente: { nome: f.nome, celular: f.celular }, endereco: { cep: digits(f.cep), rua: f.rua, numero: f.numero, complemento: f.complemento, bairro: f.bairro, cidade: f.cidade, uf: f.uf },
        pagamento_metodo: metodo, canal, utm: getUtm(), landing: sessionStorage.getItem("na_landing"), ciencia_advertencias: ciencia, observacao: f.obs, sessao,
      });
      const link = waLink(settings?.whatsapp_e164 || "5531987686700", r.data.whatsapp_texto);
      if (waWin) waWin.location.href = link;
      setItems([]);
      onDone({ ...r.data, link, canal });
    } catch (e) {
      waWin?.close();
      setErrs(fieldErrors(e));
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const field = (k, label, props = {}) => (
    <div className={props.wide ? "col-span-2" : ""}>
      <label htmlFor={`q-${k}`} className="label-na">{label}</label>
      <input id={`q-${k}`} className="input-na py-2.5" value={f[k]} onChange={props.onChange || set(k)} inputMode={props.inputMode} data-testid={`quick-${k}`} />
      {errs[k] && <p className="err-na" data-testid={`quick-error-${k}`}>{errs[k]}</p>}
    </div>
  );
  return (
    <div className="space-y-3" data-testid="quick-order-form">
      <div className="grid grid-cols-2 gap-3">
        {field("nome", "Nome completo", { wide: true })}
        {field("celular", "Celular com DDD", { inputMode: "tel", onChange: (e) => setF({ ...f, celular: maskPhone(e.target.value) }) })}
        {field("cep", "CEP", { inputMode: "numeric", onChange: onCep })}
        {field("rua", "Endereço", { wide: true })}
        {field("numero", "Número")}
        {field("complemento", "Complemento")}
        {field("bairro", "Bairro")}
        {field("cidade", "Cidade")}
      </div>
      <div role="radiogroup" aria-label="Pagamento" className="grid grid-cols-3 gap-2">
        {[["pix", "Pix 5% off"], ["cartao", "Cartão 3x"], ["boleto", "Boleto"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setMetodo(k)} className={`rounded-2xl border px-2 py-2.5 text-xs font-semibold ${metodo === k ? "border-na-forest bg-na-sage2" : "border-na-line"}`} data-testid={`quick-pay-${k}`}>{l}</button>
        ))}
      </div>
      <div><label htmlFor="q-obs" className="label-na">Observação</label><textarea id="q-obs" rows={2} className="input-na" value={f.obs} onChange={set("obs")} data-testid="quick-obs" /></div>
      {pr?.restrito && (
        <label className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-xs text-amber-900">
          <input type="checkbox" checked={ciencia} onChange={(e) => setCiencia(e.target.checked)} className="mt-0.5" data-testid="quick-warnings-checkbox" />
          Li as advertências: {pr.itens.filter((i) => i.restrito).map((i) => i.advertencias).join(" | ")}
        </label>
      )}
      {errs.ciencia_advertencias && <p className="err-na">{errs.ciencia_advertencias}</p>}
      {pr && <div className="flex justify-between rounded-2xl bg-na-sage2 p-3 text-sm"><span>Total {pr.frete ? `(frete ${pr.frete.valor ? brl(pr.frete.valor) : "grátis"})` : "(frete estimado após CEP)"}</span><b data-testid="quick-total">{brl(pr.total)}</b></div>}
      <button className="btn-na w-full" disabled={busy} onClick={() => send("vitrine")} data-testid="quick-pay-now">Pagar agora</button>
      <button className="btn-wa w-full" disabled={busy} onClick={() => send("whatsapp")} data-testid="quick-send-whatsapp"><MessageCircle className="h-4 w-4" /> Enviar pedido pelo WhatsApp</button>
    </div>
  );
}

export default function QuickShop() {
  const { quick, setQuick, items, pricing, setQty, meta } = useStore();
  const [prods, setProds] = useState([]);
  const [need, setNeed] = useState(null);
  const [done, setDone] = useState(null);
  useEffect(() => { if (quick) api.get("/products", { params: need ? { need } : {} }).then((r) => setProds(r.data)); }, [quick, need]);
  if (!quick) return null;
  const tab = done ? "pronto" : quick;
  const count = items.reduce((a, b) => a + b.quantidade, 0);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/30 sm:inset-auto sm:bottom-24 sm:right-6 sm:bg-transparent" onClick={(e) => e.target === e.currentTarget && setQuick(null)}>
      <div className="flex h-[88vh] w-full flex-col overflow-hidden rounded-t-[28px] bg-na-bg shadow-2xl sm:h-[640px] sm:w-[420px] sm:rounded-[28px] sm:border sm:border-na-line" role="dialog" aria-label="Vitrine rápida" data-testid="quickshop-panel">
        <div className="flex items-center gap-2 border-b border-na-line bg-white px-4 py-3">
          {["sacola", "pedido"].includes(tab) && <button aria-label="Voltar" onClick={() => setQuick(tab === "pedido" ? "sacola" : "produtos")} data-testid="quick-back"><ArrowLeft className="h-5 w-5" /></button>}
          <div className="flex flex-1 gap-1">
            {[["produtos", "Produtos"], ["sacola", `Sacola (${count})`], ["pedido", "Pedido"]].map(([k, l]) => (
              <button key={k} onClick={() => { setDone(null); setQuick(k); }} disabled={k === "pedido" && !items.length} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${tab === k ? "bg-na-forest text-white" : "text-na-ink2"}`} data-testid={`quick-tab-${k}`}>{l}</button>
            ))}
          </div>
          <button aria-label="Fechar" onClick={() => { setDone(null); setQuick(null); }} data-testid="quick-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {tab === "produtos" && (
            <>
              <div className="no-scrollbar flex gap-1.5 overflow-x-auto pb-2">
                <button className={`chip px-3 py-1.5 text-xs ${!need ? "chip-on" : ""}`} onClick={() => setNeed(null)}>Todos</button>
                {meta.needs.map((n) => <button key={n.chave} className={`chip px-3 py-1.5 text-xs ${need === n.chave ? "chip-on" : ""}`} onClick={() => setNeed(n.chave)} data-testid={`quick-need-${n.chave}`}>{n.nome}</button>)}
              </div>
              <ul className="divide-y divide-na-line">{prods.map((p) => <ProductRow key={p.slug} p={p} />)}</ul>
            </>
          )}
          {tab === "sacola" && (
            !items.length ? <p className="py-10 text-center text-sm text-na-ink2">Sua sacola está vazia.</p> : (
              <div className="space-y-4">
                <ul className="divide-y divide-na-line">
                  {items.map((it, idx) => {
                    const l = pricing?.itens?.find((x) => x.slug === it.slug && x.frascos === it.frascos);
                    return (
                      <li key={idx} className="flex items-center gap-3 py-3" data-testid={`quick-cart-line-${it.slug}`}>
                        <img src={fileUrl(l?.imagem)} alt="" className="prod-img h-12 w-12 object-contain" />
                        <div className="flex-1 text-sm"><p className="font-semibold">{l?.nome}</p><p className="text-xs text-na-ink2">{it.frascos} un.</p></div>
                        <button onClick={() => setQty(idx, it.quantidade - 1)} aria-label="Diminuir" className="rounded-full border p-1"><Minus className="h-3 w-3" /></button>
                        <span className="w-5 text-center text-sm">{it.quantidade}</span>
                        <button onClick={() => setQty(idx, it.quantidade + 1)} aria-label="Aumentar" className="rounded-full border p-1"><Plus className="h-3 w-3" /></button>
                        <b className="w-20 text-right text-sm">{l ? brl(l.total) : ""}</b>
                      </li>
                    );
                  })}
                </ul>
                <FreeShipBar pricing={pricing} />
                {pricing && <div className="text-sm"><div className="flex justify-between"><span>Subtotal</span><b>{brl(pricing.produtos_total)}</b></div><div className="flex justify-between text-na-forest"><span>No Pix</span><b>{brl(pricing.total_pix)}</b></div></div>}
                <button className="btn-na w-full" onClick={() => setQuick("pedido")} data-testid="quick-order-here">Fazer pedido por aqui</button>
                <a className="btn-line w-full" href={`/checkout?sacola=${buildSacola(items)}`} data-testid="quick-finish-store">Finalizar na loja virtual</a>
              </div>
            )
          )}
          {tab === "pedido" && <OrderForm onDone={setDone} />}
          {tab === "pronto" && done && (
            done.canal === "whatsapp" ? (
              <div className="space-y-3" data-testid="quick-whatsapp-ready">
                <h3 className="font-serif text-2xl">Pedido pronto no WhatsApp</h3>
                <p className="rounded-2xl bg-amber-50 p-3 text-sm text-amber-900">Seu pedido {done.numero} só chega até nós quando você tocar em <b>enviar</b> no WhatsApp.</p>
                <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-2xl border border-na-line bg-white p-3 text-xs" data-testid="quick-whatsapp-text">{done.whatsapp_texto}</pre>
                <button className="btn-line w-full" onClick={() => copyText(done.whatsapp_texto, "Mensagem copiada")} data-testid="quick-copy-message"><Copy className="h-4 w-4" /> Copiar mensagem</button>
                <a className="btn-wa w-full" href={done.link} target="_blank" rel="noreferrer" data-testid="quick-open-whatsapp"><MessageCircle className="h-4 w-4" /> Abrir o WhatsApp de novo</a>
              </div>
            ) : (
              <div className="space-y-3" data-testid="quick-payment-ready">
                <h3 className="font-serif text-2xl">Pedido {done.numero}</h3>
                <PaymentPanel order={done} />
              </div>
            )
          )}
        </div>
        {tab === "produtos" && items.length > 0 && (
          <button className="m-3 rounded-full bg-na-forest py-3.5 text-sm font-semibold text-white" onClick={() => setQuick("sacola")} data-testid="quick-view-cart">Ver sacola ({count}) · {brl(pricing?.produtos_total)}</button>
        )}
      </div>
    </div>
  );
}
